import * as React from 'react'
import { createPortal } from 'react-dom'
import ReactMarkdown from 'react-markdown'
import { X, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'

// Fallback splits on build mode so a production bundle never ships pointing at
// localhost. Dev fallback keeps the dev-server origin so frontend devs who
// haven't set the env var still see local docs; in any non-dev build the
// deployed docs origin is the only safe default.
//
// `||` (not `??`) on purpose: an empty-string VITE_DOCS_BASE_URL — a common
// misconfiguration — must be treated as unset and fall through to the
// mode-based default, not produce a same-origin `fetch('/help/...')`.
const DOCS_BASE_URL =
  import.meta.env.VITE_DOCS_BASE_URL ||
  (import.meta.env.DEV ? 'http://localhost:4321' : 'https://docs.ottotester.ai')

interface RelatedTopic {
  title: string
  description: string
  href: string
}

interface HelpTopicPayload {
  slug: string
  title: string
  summary: string
  canonicalUrl: string
  /** Optional curated next-actions list, surfaced inline in the
   * drawer as a tap-list under the summary. Populated by the topic
   * author via the `related` frontmatter block in the source MDX
   * (today only `how-to.mdx` opts in). Absence means "no list" —
   * the drawer does not synthesise one from body content. */
  related?: RelatedTopic[]
}

/** Single point of truth for "is this href docs-relative, and if
 * so, what's the absolute URL on the docs origin?" Used by both the
 * Markdown body anchor renderer (`DocsLink`) and the related-topics
 * list to avoid two implementations drifting apart.
 *
 * Returns `null` for hrefs that should not be linked at all
 * (typically unsafe schemes — `javascript:`, `data:`, …). Callers
 * render the original child text as plain prose in that case so
 * the author's intent isn't entirely silent.
 *
 * Resolution rules — order matters (per Copilot review on #990):
 *
 *   1. Protocol-relative (`//host/path`): passed through verbatim.
 *      The browser resolves the scheme from the current page; this
 *      is the standard semantics and must NOT be mangled by
 *      `${DOCS_BASE_URL}<href>` concatenation, which would have
 *      produced `${DOCS_BASE_URL}//host/path` (a different
 *      destination).
 *
 *   2. Scheme-bearing (`http:`, `https:`, `mailto:`, `tel:`, …):
 *      allowlisted to the four schemes a summary or related-list
 *      author would legitimately use. Everything else returns
 *      `null` — this blocks `javascript:` and `data:` URIs from
 *      becoming clickable affordances in the drawer. The
 *      enumeration is a hard allowlist by design; the cost of
 *      adding a new safe scheme later is one line, the cost of an
 *      XSS vector via `javascript:` is much higher.
 *
 *   3. Anything else (root-relative `/path`, fragment `#anchor`,
 *      no-leading-slash `path`): resolved via `new URL(href,
 *      base)` against the topic's canonical page on the docs
 *      origin. `new URL` handles all three cases correctly:
 *      `/quickstart` → docs root + `/quickstart`; `#step-3` →
 *      canonical page + `#step-3`; `quickstart` → canonical
 *      page's parent + `quickstart`. The first two are the
 *      authored cases today; the third is supported defensively
 *      since `${DOCS_BASE_URL}quickstart` (string concat) would
 *      have produced an invalid URL. */
const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:'])
const SCHEME_PREFIX = /^([a-z][a-z0-9+.-]*):/i

function resolveDocsHref(href: string, basePathname: string): string | null {
  if (!href) return null
  // Protocol-relative — browser-resolved scheme, never rewritten.
  if (href.startsWith('//')) return href
  // Scheme-bearing — allowlist gate.
  const schemeMatch = href.match(SCHEME_PREFIX)
  if (schemeMatch) {
    const scheme = `${schemeMatch[1].toLowerCase()}:`
    return SAFE_SCHEMES.has(scheme) ? href : null
  }
  // Docs-relative — resolve against the canonical page so fragments
  // and no-leading-slash paths anchor at the right docs URL.
  try {
    return new URL(href, `${DOCS_BASE_URL}${basePathname}`).href
  } catch {
    return null
  }
}

/** Curated next-actions list rendered under the summary when the
 * topic carries a `related` frontmatter block. Each row links to a
 * deep docs URL (canonicalised via `resolveDocsHref`) in a new tab.
 * The card-rich body layout on the full docs page is intentionally
 * not mirrored here — the drawer is narrow + scrollable, so the
 * highest-value rendering is a clean two-line tap-list: bold title,
 * one-line description.
 *
 * The list is purposely outside the `prose`-style className block
 * around the Markdown summary so its rows don't pick up
 * underline-on-link styling — the description text reads better
 * unmarked. */
function RelatedList({ items }: { items: RelatedTopic[] }) {
  // Related hrefs are authored from the docs-root perspective (always
  // root-relative or scheme-bearing — see the `related` schema in
  // apps/docs/src/content.config.ts), so the resolver's base path
  // doesn't need to track the topic's canonical URL. Pass '/' so a
  // future careless `quickstart` (no leading slash) at least resolves
  // off the docs root rather than against the topic page.
  return (
    <ul className="mt-5 space-y-2 border-t pt-4" aria-label="Related actions">
      {items.map((item) => {
        const resolved = resolveDocsHref(item.href, '/')
        // Schema validation upstream guarantees a non-empty href, so
        // `resolved === null` can only happen if a topic author
        // somehow slipped an unsafe scheme past Zod (defense in
        // depth — skip the row entirely rather than render a
        // text-only stub that promises a click).
        if (resolved === null) return null
        return (
          <li key={item.href}>
            <a
              href={resolved}
              target="_blank"
              rel="noopener noreferrer"
              className="group block rounded-md px-2 py-1.5 -mx-2 hover:bg-muted focus:outline-hidden focus:ring-2 focus:ring-ring"
            >
              <div className="text-sm font-medium text-foreground group-hover:text-primary">
                {item.title}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
                {item.description}
              </div>
            </a>
          </li>
        )
      })}
    </ul>
  )
}

/** Anchor renderer factory for the Markdown summary. Captures the
 * topic's canonical URL so fragments and no-leading-slash relatives
 * inside the summary resolve against the right docs page. Returns
 * a per-render component that ReactMarkdown invokes per anchor.
 *
 * When `resolveDocsHref` returns `null` (unsafe scheme — see its
 * docstring for the allowlist), the navigation affordance is
 * dropped and the link's child text renders as plain prose. The
 * author's intent stays visible; the unsafe href cannot fire. */
function makeDocsLink(basePathname: string) {
  return function DocsLink(props: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
    const { href, children, ...rest } = props
    if (!href) return <a {...rest}>{children}</a>
    const resolved = resolveDocsHref(href, basePathname)
    if (resolved === null) return <span>{children}</span>
    return (
      <a
        {...rest}
        href={resolved}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    )
  }
}

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; topic: HelpTopicPayload }
  | { status: 'error'; message: string }

interface HelpPanelProps {
  slug: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function HelpPanel({ slug, open, onOpenChange }: HelpPanelProps) {
  const [state, setState] = React.useState<FetchState>({ status: 'idle' })
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)
  const previouslyFocusedRef = React.useRef<Element | null>(null)

  React.useEffect(() => {
    if (!open) return
    let cancelled = false
    setState({ status: 'loading' })
    fetch(`${DOCS_BASE_URL}/help/${encodeURIComponent(slug)}.json`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`help fetch failed: ${res.status}`)
        return (await res.json()) as HelpTopicPayload
      })
      .then((topic) => {
        if (!cancelled) setState({ status: 'ready', topic })
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'unknown error'
          setState({ status: 'error', message })
        }
      })
    return () => {
      cancelled = true
    }
  }, [open, slug])

  // Modal focus management: capture the previously focused element on open,
  // shift focus to the close button so keyboard users land somewhere inside
  // the dialog, and restore on close. ESC closes. A full tab-trap is a
  // follow-up — see PR #626 review thread.
  React.useEffect(() => {
    if (!open) return
    previouslyFocusedRef.current = document.activeElement
    closeButtonRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const previous = previouslyFocusedRef.current
      if (previous instanceof HTMLElement) previous.focus()
    }
  }, [open, onOpenChange])

  if (!open) return null
  // `createPortal` needs a real DOM node — bail in non-DOM environments
  // (SSR / prerender) rather than throwing. The client-only SPA always
  // has `document` at render time; this is a safety net, not a hot path.
  if (typeof document === 'undefined') return null

  // Portal to <body> so the modal escapes its mount point. Same fix as
  // `Dialog` (see `components/ui/dialog.tsx`): `position: fixed`
  // resolves against the nearest ancestor with a `transform`, `filter`,
  // or `backdrop-filter` — not the viewport. HelpPanel is mounted
  // inside the top `<header>`, which sets `backdrop-blur-sm`; without
  // the portal the whole drawer clips to the 56px header strip and
  // appears half-hidden. The dim overlay + the slide-in panel are both
  // `position: fixed`, so both need to escape the header's containing
  // block.
  return createPortal(
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Help">
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-xs"
        onClick={() => onOpenChange(false)}
      />
      <div
        className={cn(
          'fixed right-0 top-0 bottom-0 z-50 w-full max-w-md',
          'flex flex-col bg-background border-l shadow-xl',
          'animate-in slide-in-from-right',
        )}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b shrink-0">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Help</div>
            {state.status === 'ready' && (
              <h2 className="text-lg font-semibold truncate">{state.topic.title}</h2>
            )}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-sm opacity-70 hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring"
            aria-label="Close help"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {state.status === 'loading' && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {state.status === 'error' && (
            <p className="text-sm text-destructive">
              Couldn't load help for <code>{slug}</code>: {state.message}
            </p>
          )}
          {state.status === 'ready' && (
            <>
              <div className="text-sm leading-6 space-y-3 [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline">
                <ReactMarkdown
                  components={{ a: makeDocsLink(state.topic.canonicalUrl) }}
                >
                  {state.topic.summary}
                </ReactMarkdown>
              </div>
              {state.topic.related && state.topic.related.length > 0 && (
                <RelatedList items={state.topic.related} />
              )}
            </>
          )}
        </div>

        {state.status === 'ready' && (
          <div className="px-6 py-4 border-t shrink-0">
            <a
              href={`${DOCS_BASE_URL}${state.topic.canonicalUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              Read full docs
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
